import qs from 'qs';
import type { FetchContext, FetchOptions, FetchResponse } from 'ofetch';
import type { $Fetch } from 'nitropack';

import type { RefreshTokenRequest } from '~~/modules/auth/runtime/types';

type TokenResponseData = { access_token: string; refresh_token: string };

export default defineNuxtPlugin((nuxtApp) => {
    const authBaseURL = useRuntimeConfig().public.authBaseURL;
    const authOptions = useRuntimeConfig().public.auth;

    const userSession = useUserSessionStore();

    const $apiFetch: $Fetch = $fetch.create({
        retry: 0,
        onRequest(context) {
            setRequestHeaders(context.options);
        },

        async onResponse(context) {
            if (isInvalidToken(context.response)) {
                const tokenRefreshed = await tryRefreshToken();

                if (tokenRefreshed) {
                    // Set headers and retry the original request
                    setRequestHeaders(context.options);

                    return await $fetch<any>(context.request, {
                        ...(context.options as FetchOptions & { method: 'GET' | 'POST' | 'PUT' | 'DELETE' }),
                        async onResponse(ctx) {
                            // Rewrite the response
                            transformResponse(ctx);
                            Object.assign(context, ctx);
                        },
                    });
                } else userSession.clear();
            } else
                transformResponse(context);
        },
    });
    
    async function tryRefreshToken() {
        if (!authOptions.scope.includes('offline_access')) return false;

        const data: RefreshTokenRequest = {
            client_id: authOptions.clientId,
            grant_type: 'refresh_token',
            refresh_token: userSession.refreshToken,
        };

        try {
            const response = await $fetch<TokenResponseData>(authOptions.tokenURL, {
                body: qs.stringify(data),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                baseURL: authBaseURL,
                method: 'POST',
            });

            userSession.setTokens(response.access_token, response.refresh_token);

            return true;
        } catch {
            return false;
        }
    }

    function isInvalidToken(response: FetchResponse<any>) {
        if (!response?.headers) return false;

        const wwwAuthenticateHeader = response.headers.get('www-authenticate');

        return (
            response.status === 401 &&
            wwwAuthenticateHeader &&
            (wwwAuthenticateHeader.includes('invalid_token') || wwwAuthenticateHeader.includes('expired_token'))
        );
    }

    function setRequestHeaders(options: FetchOptions) {
        // Normalize headers
        options.headers = options.headers instanceof Headers ? options.headers : new Headers(options.headers);

        if (userSession.accessToken) setHeader(options.headers, 'Authorization', 'Bearer ' + userSession.accessToken);
    }

    function setHeader(headers: Headers | undefined, name: string, value?: string) {
        if (!headers || !value) return;

        headers.set(name, value);
    }

    // Used to convert ISO 8601 date strings to Date objects
    function transformResponse(context: FetchContext) {
        const response = context.response;
        if (!response) return;

        if (!(response.status >= 200 && response.status < 300))
            return;

        if (!response.headers.get('content-type')?.includes('application/json'))
            return;
        
        if (!(response._data && typeof response._data === 'object'))
            return;

        try {
            // The response is ALREADY parsed by ofetch into object (using JSON.parse), so we need to stringify it first
            const responseAsJSON = JSON.stringify(response._data);

            // And re-parse, but this time with date conversion
            if (responseAsJSON)
                response._data = JSON.parse(responseAsJSON, jsonISO8601DatesReviver);
        } catch {}
    }

    function jsonISO8601DatesReviver(key: any, value: any) {
        // RegExp to test a string for a full ISO 8601 Date
        // Does not do any sort of date validation, only checks if the string is according to the ISO 8601 spec
        // YYYY-MM-DDThh:mm:ss
        // YYYY-MM-DDThh:mm:ssTZD
        // YYYY-MM-DDThh:mm:ss.sTZD
        const dateFormat = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;

        if (typeof value === "string" && dateFormat.test(value)) {
            return new Date(value);
        }

        return value;
    }

    return {
        provide: {
            apiFetch: $apiFetch,
        },
    };
});

declare module '#app' {
    interface NuxtApp {
        $apiFetch: $Fetch;
    }
}
